/**
 * NotificationContext — app-wide replacement for native alert()/confirm().
 *
 * toast.success(message) / toast.error(message): dismiss-only banner, auto-closes.
 * toast.undo(message, onUndo): fires an already-completed reversible action's
 *   toast with an "Deshacer" button; clicking it runs onUndo. Use this instead
 *   of confirm() when the action can be trivially reversed (a delete that's
 *   really an archive, a status flip, etc.) — skip the blocking dialog, act
 *   immediately, and offer a few seconds to change their mind.
 * confirm(message, options): returns a Promise<boolean>, resolved by the user's choice.
 * Mirrors the PlanContext/UpgradeModal pattern already used in this app.
 *
 * Every toast (success/error/undo) also gets appended to `history` — a
 * capped, most-recent-first list persisted to localStorage per user — so
 * NotificationBell.js can show "what happened while I wasn't looking"
 * instead of only new leads. A toast still disappears from the stack after
 * its usual timeout; only its record in `history` sticks around.
 */
import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import ToastStack from '../components/shared/ToastStack';
import ConfirmDialog from '../components/shared/ConfirmDialog';
import { useAuth } from './AuthContext';

const NotificationContext = createContext(null);

let toastIdCounter = 0;
const HISTORY_LIMIT = 30;

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState(null);
  const resolveRef = useRef(null);

  const historyKey = user?.id ? `notification_history_${user.id}` : null;
  const [history, setHistory] = useState([]);

  // Reload history when the logged-in user changes (e.g. after login, or
  // "Ver como usuario" impersonation) — the key above is per-user, so the
  // in-memory list has to be re-read from storage rather than staying
  // stale from whoever was logged in before.
  useEffect(() => {
    if (!historyKey) { setHistory([]); return; }
    try {
      const stored = JSON.parse(localStorage.getItem(historyKey) || '[]');
      setHistory(Array.isArray(stored) ? stored : []);
    } catch {
      setHistory([]);
    }
  }, [historyKey]);

  const pushHistory = useCallback((message, type) => {
    if (!historyKey) return;
    setHistory((prev) => {
      const next = [{ id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, message, type, timestamp: Date.now() }, ...prev].slice(0, HISTORY_LIMIT);
      try { localStorage.setItem(historyKey, JSON.stringify(next)); } catch {}
      return next;
    });
  }, [historyKey]);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const pushToast = useCallback((message, type) => {
    const id = ++toastIdCounter;
    setToasts((prev) => [...prev, { id, message, type }]);
    pushHistory(message, type);
    setTimeout(() => dismissToast(id), 4000);
  }, [dismissToast, pushHistory]);

  const pushUndoToast = useCallback((message, onUndo, duration = 6000) => {
    const id = ++toastIdCounter;
    const timer = setTimeout(() => dismissToast(id), duration);
    const handleUndo = () => {
      clearTimeout(timer);
      dismissToast(id);
      onUndo?.();
    };
    setToasts((prev) => [...prev, { id, message, type: 'undo', onUndo: handleUndo }]);
    pushHistory(message, 'undo');
  }, [dismissToast, pushHistory]);

  const toast = {
    success: useCallback((message) => pushToast(message, 'success'), [pushToast]),
    error: useCallback((message) => pushToast(message, 'error'), [pushToast]),
    undo: useCallback((message, onUndo, duration) => pushUndoToast(message, onUndo, duration), [pushUndoToast]),
  };

  const confirm = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setConfirmState({ message, options });
    });
  }, []);

  const resolveConfirm = (result) => {
    if (resolveRef.current) resolveRef.current(result);
    resolveRef.current = null;
    setConfirmState(null);
  };

  const value = { toast, confirm, history };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <ToastStack toasts={toasts} onDismiss={dismissToast} />
      {confirmState && (
        <ConfirmDialog
          message={confirmState.message}
          options={confirmState.options}
          onConfirm={() => resolveConfirm(true)}
          onCancel={() => resolveConfirm(false)}
        />
      )}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotification must be used inside <NotificationProvider>');
  return ctx;
};

export default NotificationContext;
